import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const alertVariants = cva(
  "group/alert relative grid w-full gap-0.5 rounded-[var(--fl-radius-4)] border px-3 py-2 text-left text-ui-14 text-[var(--fl-color-content-default-default)] has-data-[part=alert-action]:relative has-data-[part=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4",
  {
    // The tones match the modal captions: success, alert (warning), error and info.
    variants: {
      /**
       * The tone of the message: neutral by default, or `info`, `success`, `alert` (a warning) or `error`;
       * `destructive` is the same as `error`.
       */
      variant: {
        default:
          "border-[var(--fl-color-border-outlined-default)] bg-[var(--fl-color-background-page-foreground)] text-[var(--fl-color-content-default-default)]",
        success:
          "border-[var(--fl-color-border-success-default)] bg-[var(--fl-color-background-success-secondary)] *:data-[part=alert-title]:text-[var(--fl-color-content-success-default)] *:[svg]:text-[var(--fl-color-content-success-default)]",
        alert:
          "border-[var(--fl-color-border-alert-default)] bg-[var(--fl-color-background-alert-secondary)] *:data-[part=alert-title]:text-[var(--fl-color-content-alert-default)] *:[svg]:text-[var(--fl-color-content-alert-default)]",
        error:
          "border-[var(--fl-color-border-error-default)] bg-[var(--fl-color-background-error-secondary)] *:data-[part=alert-title]:text-[var(--fl-color-content-error-primary)] *:[svg]:text-[var(--fl-color-content-error-primary)]",
        info: "border-[var(--fl-color-border-info-default)] bg-[var(--fl-color-background-info-secondary)] *:data-[part=alert-title]:text-[var(--fl-color-content-info-default)] *:[svg]:text-[var(--fl-color-content-info-default)]",
        /** Same as `error`. */
        destructive:
          "border-[var(--fl-color-border-error-default)] bg-[var(--fl-color-background-error-secondary)] *:data-[part=alert-title]:text-[var(--fl-color-content-error-primary)] *:[svg]:text-[var(--fl-color-content-error-primary)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

/**
 * An inline message about a state the user should know of, such as a warning or a failure. Put an `AlertTitle`, an
 * `AlertDescription` and optionally actions inside; for a message that disappears by itself use a toast instead.
 */
function Alert({ className, variant, ...props }: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return <div data-part="alert" role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
}

/** The short heading of an `Alert`, coloured by the alert's `variant`. */
function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-part="alert-title"
      className={cn(
        "font-semibold group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground",
        className
      )}
      {...props}
    />
  )
}

/** The explanation below the title of an `Alert`. */
function AlertDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-part="alert-description"
      className={cn(
        "text-ui-14 text-balance text-[var(--fl-color-content-default-secondary)] md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4",
        className
      )}
      {...props}
    />
  )
}

/** One control, such as a close button, pinned to the top-right corner of an `Alert`. */
function AlertAction({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-part="alert-action" className={cn("absolute top-2 right-2", className)} {...props} />
}

/** Actions below the message, in the flow of the alert, such as a retry or a dismiss. AlertAction instead pins one control to the corner. */
function AlertActions({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-part="alert-actions"
      className={cn("flex flex-wrap items-center gap-2 pt-2 group-has-[>svg]/alert:col-start-2", className)}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription, AlertAction, AlertActions, alertVariants }
